/**
 * Users & roles of the signed-in tenant (backend-node lib/team.js, `/api/v1/team…`), for admins
 * (SUPERADMIN, TENANTADMIN) only. Users get only custom roles here; admins are managed in the
 * superadmin panel. A new user's and a reset password come back once, as `passphrase`.
 */
import { api } from '@/api/client'
import { APP_BASE } from '@/app/base'

export const TEAM_PATH = '/team'
/** Approve, dismiss and reopen recommendations: a permission apart from seeing the page. */
export const DECIDE = 'action:decide'
/** The set-password e-mail's languages (backend-node lib/mail/password-link-email.js). */
export const MAIL_LANGUAGES = [
    { code: 'et', label: 'Eesti' },
    { code: 'en', label: 'English' },
    { code: 'de', label: 'Deutsch' },
    { code: 'ru', label: 'Русский' },
] as const

/** Where a set-password link points: this app, as the browser sees it (the API checks the host). */
export function linkBase(): string {
    return `${window.location.origin}${APP_BASE}`
}

export interface LinkSent {
    email: string
    expires_hours: number
    lang: string
}

export interface TeamRole {
    id: number
    name: string
    system: boolean
    users: number
    /** `['*']` for a system role, else `page:<view id>` and `action:<name>` grants. */
    permissions: string[]
}

export interface TeamUser {
    id: number
    name: string
    email: string
    enabled: boolean
    role: { id: number; name: string; system: boolean }
    last_login: string | null
    created_at: string | null
    is_self: boolean
}

export interface Team {
    users: TeamUser[]
    roles: TeamRole[]
    me: { user_id: number; role: string }
}

type Envelope<T> = { success: boolean; data: T }

export async function fetchTeam(signal?: AbortSignal): Promise<Team> {
    return (await api.get<Envelope<Team>>('/api/v1/team', { signal })).data
}

/**
 * Nothing is e-mailed unless `send_link`: then the answer has `link` and no passphrase, or, if the
 * e-mail failed, the passphrase and `mail_error` (the user exists either way).
 */
export async function addUser(input: { name: string; email: string; role_id: number; send_link?: boolean; lang?: string }) {
    const body = input.send_link ? { ...input, link_base: linkBase() } : input
    return (await api.post<Envelope<{ user: TeamUser; passphrase?: string; link?: LinkSent; mail_error?: string }>>('/api/v1/team/users', body)).data
}

/** E-mails the user a link to set their own password (72 hours, once). */
export async function sendPasswordLink(userId: number, lang: string) {
    return (await api.post<Envelope<{ id: number; link: LinkSent }>>(`/api/v1/team/users/${userId}/password-link`, { lang, link_base: linkBase() })).data
}

export async function changeUserRole(userId: number, roleId: number) {
    return (await api.patch<Envelope<{ id: number }>>(`/api/v1/team/users/${userId}`, { role_id: roleId })).data
}

export async function resetPassword(userId: number) {
    return (await api.post<Envelope<{ id: number; passphrase: string }>>(`/api/v1/team/users/${userId}/reset-password`)).data
}

export async function removeUser(userId: number) {
    return (await api.post<Envelope<{ id: number }>>(`/api/v1/team/users/${userId}/remove`)).data
}

/** A new custom role, with no pages or with another role's permissions to start from. */
export async function addRole(name: string, permissions: string[] = []) {
    return (await api.post<Envelope<TeamRole>>('/api/v1/team/roles', { name, permissions })).data
}

export async function updateRole(roleId: number, changes: { name?: string; permissions?: string[] }) {
    return (await api.patch<Envelope<{ id: number; name: string; permissions?: string[] }>>(`/api/v1/team/roles/${roleId}`, changes)).data
}

export async function deleteRole(roleId: number) {
    return (await api.post<Envelope<{ id: number }>>(`/api/v1/team/roles/${roleId}/delete`)).data
}
